













































































































































































































import Vue from "vue";
import { indexBy, isEmpty, isNotEmpty, uniqby } from "@/fp";
import AppCheckbox from "@/components/AppCheckbox.vue";
import { computed, onMounted, Ref, ref, watch } from "@vue/composition-api";
import {
  InformationType,
  InformationTypeTree,
  InfoTypeHelper,
} from "@/common/models";
import { SharedState } from "@/common/state";
import PassivePopup from "@/components/PassivePopup.vue";

export default Vue.extend({
  components: { AppCheckbox, PassivePopup },
  props: {
    onlyGroup: {
      type: String,
      default: "",
    },
    default_id: {
      type: String,
      default: "",
    },
    selected: {
      type: Array,
      default() {
        return [];
      },
    },
    multi: {
      type: Boolean,
      default: false,
    },
    with_apply: {
      type: Boolean,
      default: false,
    },
    exclude_folder_labels: {
      type: Boolean,
      default: false,
    },
  },
  // @ts-ignore
  setup(props, { emit }) {
    const query = ref("");
    const is_searching = computed(() => query.value.length > 0);

    const show_popup = ref(false);
    const info_types_tree: Ref<InformationTypeTree[]> = ref([]);
    const indexed_types_tree: Ref<Record<string, InformationType>> = ref({});
    const flattened_cache: Ref<
      Record<string, Array<InformationType & { parent_name: string }>>
    > = ref({});
    const _selected_items = computed(() => props.selected as string[]);

    const ids_arr_to_index = (ids: string[]) =>
      ids.reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as { [key: string]: boolean });

    const selected_items_display = computed(() => {
      if (_selected_items.value.length > 0) {
        return _selected_items.value
          .filter((id) => indexed_types_tree.value[id])
          .map((id) => {
            const {
              labelName,
              generation,
              parentId,
            } = indexed_types_tree.value[id];
            return (
              labelName +
              (generation === 0
                ? ""
                : ` (${SharedState.allInfoTypes.value[parentId!]?.labelName})`)
            );
          })
          .join(",");
      } else {
        return "Select information type";
      }
    });
    const selected_items_count = computed(() => _selected_items.value.length);

    const run_when_idle = (cb: () => void) => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        // @ts-ignore
        window.requestIdleCallback(cb);
      } else {
        setTimeout(cb, 0);
      }
    };

    const render_items = ref(false);
    const schedule_render_items = () => {
      render_items.value = false;
      if (typeof window !== "undefined" && "requestAnimationFrame" in window) {
        // @ts-ignore
        window.requestAnimationFrame(() => {
          render_items.value = true;
        });
      } else {
        setTimeout(() => {
          render_items.value = true;
        }, 0);
      }
    };

    const pending_multi_selected_items_ids = ref({});

    const open_ids: Ref<Record<string, true>> = ref({});

    const filter_out_folder_label_type = (
      items: InformationTypeTree[]
    ): InformationTypeTree[] => {
      return items
        .filter((item: any) => item.labelType !== "Folder")
        .map((item) => {
          if (item.nested && item.nested.length > 0) {
            return {
              ...item,
              nested: filter_out_folder_label_type(item.nested),
            };
          }
          return item;
        });
    };

    const build_indexed_types = (items: InformationTypeTree[]) => {
      const indexed: Record<string, InformationType> = {};
      const traverse = (nodes: InformationTypeTree[]) => {
        nodes.forEach((node) => {
          indexed[node.id] = node as InformationType;
          if (node.nested && node.nested.length > 0) {
            traverse(node.nested);
          }
        });
      };
      traverse(items);
      return indexed;
    };

    const on_open_details_click = (
      ev: PointerEvent,
      index: number,
      id: string
    ) => {
      const el = document
        .querySelectorAll(`details`)
        .item(index) as HTMLDetailsElement;
      switch (true) {
        case isNotEmpty(open_ids.value[id]) && el.open:
          delete open_ids.value[id];
          open_ids.value = { ...open_ids.value };
          break;
        case isNotEmpty(open_ids.value[id]) && !el.open:
          // do nothing
          break;
        case isEmpty(open_ids.value[id]) && !el.open:
          open_ids.value = { ...open_ids.value, [id]: true };
          break;
        case isEmpty(open_ids.value[id]) && el.open:
          delete open_ids.value[id];
          open_ids.value = { ...open_ids.value };
          break;
      }
    };

    const reset_open_ids = () => {
      open_ids.value = {};
      setTimeout(() => {
        const next_open_ids: Record<string, true> = {};
        if (display_items.value && display_items.value.length === 1) {
          next_open_ids[display_items.value[0].id] = true;
        }
        const selected_ids = normalize_selected_ids(props.selected as string[]);
        selected_ids.forEach((selected_id) => {
          const ancestors = get_ancestor_ids(selected_id);
          ancestors.forEach((ancestor_id) => {
            next_open_ids[ancestor_id] = true;
          });
        });
        open_ids.value = next_open_ids;
      }, 200);
    };

    const sync_info_types_tree = () => {
      const tree = SharedState.get_raw_info_types_tree();
      info_types_tree.value = props.exclude_folder_labels
        ? filter_out_folder_label_type(tree)
        : tree;
      indexed_types_tree.value = build_indexed_types(info_types_tree.value);
    };

    onMounted(() => {
      reset_open_ids();
      sync_info_types_tree();
      if (props.multi) {
        pending_multi_selected_items_ids.value = ids_arr_to_index(
          normalize_selected_ids(props.selected as string[])
        );
      }
      run_when_idle(() => {
        build_flat_items(info_types_tree.value);
      });
    });

    const group_cache_key = ref("initial");

    watch(
      () => [props.onlyGroup, info_types_tree.value.length],
      () => {
        flattened_cache.value = {} as Record<
          string,
          Array<InformationType & { parent_name: string }>
        >;
        group_cache_key.value = `${Date.now()}`;
      }
    );

    const display_items_cache: Ref<
      Record<string, InformationTypeTree[]>
    > = ref({});

    const create_display_items_cache_key = () => {
      const groupId = props.onlyGroup || "__all__";
      const rootVersion = SharedState.allInfoTypes.value
        ? Object.keys(SharedState.allInfoTypes.value).length
        : 0;
      return `${groupId}-${rootVersion}`;
    };

    watch(
      () => [props.onlyGroup, Object.keys(SharedState.allInfoTypes.value).length],
      () => {
        sync_info_types_tree();
        display_items_cache.value = {};
        run_when_idle(() => {
          build_flat_items(info_types_tree.value);
        });
      }
    );

    watch(
      () => show_popup.value,
      (is_open) => {
        if (is_open) {
          schedule_render_items();
        } else {
          render_items.value = false;
        }
      }
    );

    const normalize_group_id = (id?: string) => {
      if (!id) return "";
      let resolved_id = id;
      let item = indexed_types_tree.value[resolved_id];
      while (item && item.generation > 1 && item.parentId) {
        resolved_id = item.parentId;
        item = indexed_types_tree.value[resolved_id];
      }
      return item ? resolved_id : "";
    };

    const display_items = computed(() => {
      const cache_key = create_display_items_cache_key();
      if (display_items_cache.value[cache_key]) {
        return display_items_cache.value[cache_key];
      }
      const L0_or_L1_id = normalize_group_id(props.onlyGroup as string | undefined);
      if (L0_or_L1_id) {
        const info_type: InformationType =
          indexed_types_tree.value[L0_or_L1_id];
        if (!info_type) {
          display_items_cache.value[cache_key] = [];
          return [];
        }
        switch (info_type.generation) {
          case 1:
            const parent_id = info_type.parentId;
            display_items_cache.value[cache_key] = info_types_tree.value
              .filter((l) => l.id === parent_id)
              .map((l) => l.nested)
              .flat(1)
              .filter((l) => l.id === L0_or_L1_id);
            return display_items_cache.value[cache_key];
          case 0:
          default:
            display_items_cache.value[cache_key] = info_types_tree.value.filter(
              (l) => l.id === L0_or_L1_id
            );
            return display_items_cache.value[cache_key];
        }
      } else {
        if (SharedState.FeatureHelper.is_enabled("expose_other")) {
          display_items_cache.value[cache_key] = info_types_tree.value;
          return display_items_cache.value[cache_key];
        } else {
          display_items_cache.value[cache_key] = info_types_tree.value.filter(
            (d) => d.id !== SharedState.default_label_id.value
          );
          return display_items_cache.value[cache_key];
        }
      }
    });

    const build_flat_items = (
      items: InformationTypeTree[]
    ): Array<InformationType & { parent_name: string }> => {
      const cache_key = `${group_cache_key.value}:${items
        .map((i) => i.id)
        .join(",")}`;
      if (!flattened_cache.value[cache_key]) {
        const flattened: Array<InformationType & { parent_name: string }> = [];
        const traverse = (nodes: InformationTypeTree[]) => {
          nodes.forEach((node) => {
            flattened.push({
              ...node,
              parent_name: InfoTypeHelper.l0_name_by_code(node.code),
            } as InformationType & { parent_name: string });
            if (node.nested && node.nested.length > 0) {
              traverse(node.nested);
            }
          });
        };
        traverse(items);
        flattened_cache.value[cache_key] = flattened;
      }
      return flattened_cache.value[cache_key];
    };
    // @ts-ignore
    const flat_items = computed(() => build_flat_items(display_items.value));

    const search_items = computed(() => {
      const q = query.value.toLowerCase();
      return uniqby("id")(
         // @ts-ignore
        flat_items.value.filter((l) => l.labelName.toLowerCase().startsWith(q))
      );
    });

    const selected_labels_ids = computed(() =>
      ids_arr_to_index(props.selected as string[])
    );

    const tree_relationships = computed(() => {
      const children_by_parent: Record<string, string[]> = {};
      const ancestors_by_id: Record<string, string[]> = {};

      const traverse = (nodes: InformationTypeTree[], ancestors: string[]) => {
        nodes.forEach((node) => {
          ancestors_by_id[node.id] = ancestors;
          if (ancestors.length > 0) {
            const parent_id = ancestors[ancestors.length - 1];
            if (!children_by_parent[parent_id]) {
              children_by_parent[parent_id] = [];
            }
            children_by_parent[parent_id].push(node.id);
          }
          if (node.nested && node.nested.length > 0) {
            traverse(node.nested, [...ancestors, node.id]);
          }
        });
      };

      traverse(info_types_tree.value, []);
      return { children_by_parent, ancestors_by_id };
    });

    const get_ancestor_ids = (id: string): string[] =>
      tree_relationships.value.ancestors_by_id[id] || [];

    const get_descendant_ids = (id: string): string[] => {
      const descendants: string[] = [];
      const stack = [...(tree_relationships.value.children_by_parent[id] || [])];
      while (stack.length > 0) {
        const child_id = stack.pop()!;
        descendants.push(child_id);
        const nested = tree_relationships.value.children_by_parent[child_id] || [];
        if (nested.length > 0) {
          stack.push(...nested);
        }
      }
      return descendants;
    };

    const get_leaf_ids_in_subtree = (id: string): string[] => {
      const leaf_ids: string[] = [];
      const stack = [id];
      while (stack.length > 0) {
        const node_id = stack.pop()!;
        const children = tree_relationships.value.children_by_parent[node_id] || [];
        if (children.length === 0) {
          leaf_ids.push(node_id);
          continue;
        }
        stack.push(...children);
      }
      return leaf_ids;
    };

    const normalize_selected_ids = (ids: string[]): string[] => {
      const indexed = ids_arr_to_index(
        ids.filter((id) => Boolean(indexed_types_tree.value[id]))
      );
      const sorted_ids = Object.keys(indexed).sort((a, b) => {
        const da = get_ancestor_ids(a).length;
        const db = get_ancestor_ids(b).length;
        return db - da;
      });
      // Keep more specific selections (children) and drop overlapping ancestors.
      sorted_ids.forEach((id) => {
        if (!(id in indexed)) return;
        const ancestors = get_ancestor_ids(id);
        ancestors.forEach((ancestor_id) => {
          delete indexed[ancestor_id];
        });
      });
      return Object.keys(indexed);
    };

    const current_selected_label_ids = computed(() => {
      if (props.multi) {
        const normalized_ids = normalize_selected_ids(
          Object.keys(pending_multi_selected_items_ids.value)
        );
        return ids_arr_to_index(normalized_ids);
      } else {
        return selected_labels_ids.value;
      }
    });

    const current_visual_selected_label_ids = computed(() => {
      const visual_selected_ids = {
        ...current_selected_label_ids.value,
      } as Record<string, boolean>;

      Object.keys(current_selected_label_ids.value).forEach((selected_id) => {
        const descendants = get_descendant_ids(selected_id);
        descendants.forEach((descendant_id) => {
          visual_selected_ids[descendant_id] = true;
        });
      });

      const explicit_selected_ids = current_selected_label_ids.value;
      const full_selection_cache: Record<string, boolean> = {};
      const is_subtree_fully_selected = (id: string): boolean => {
        if (id in full_selection_cache) {
          return full_selection_cache[id];
        }
        if (id in explicit_selected_ids) {
          full_selection_cache[id] = true;
          return true;
        }
        const children = tree_relationships.value.children_by_parent[id] || [];
        if (children.length === 0) {
          full_selection_cache[id] = false;
          return false;
        }
        const is_full = children.every((child_id) =>
          is_subtree_fully_selected(child_id)
        );
        full_selection_cache[id] = is_full;
        return is_full;
      };
      Object.keys(indexed_types_tree.value).forEach((id) => {
        if (is_subtree_fully_selected(id)) {
          visual_selected_ids[id] = true;
        }
      });

      return visual_selected_ids;
    });

    const set_pending_selected_ids = (next_selected_ids: Record<string, boolean>) => {
      const normalized_ids = normalize_selected_ids(Object.keys(next_selected_ids));
      pending_multi_selected_items_ids.value = ids_arr_to_index(normalized_ids);
    };

    const apply_changes = () => {
      const ids = normalize_selected_ids(
        Object.keys(pending_multi_selected_items_ids.value)
      );
      pending_multi_selected_items_ids.value = ids_arr_to_index(ids);
      emit("on_change", ids);
      show_popup.value = false;
      query.value = "";
      reset_open_ids();
    };

    const on_change = (type: InformationType) => {
      console.log("on change : ", type.id, type.labelName);
      if (props.multi) {
        const is_effectively_selected =
          type.id in current_visual_selected_label_ids.value;
        const should_select = !is_effectively_selected;

        if (!should_select) {
          // If this node is inherited from a selected parent, convert parent selection
          // into explicit leaves, excluding this subtree, so unchecking feels intuitive.
          if (
            is_effectively_selected &&
            !(type.id in pending_multi_selected_items_ids.value)
          ) {
            const next_selected_ids = {
              ...pending_multi_selected_items_ids.value,
            } as Record<string, boolean>;
            const selected_ancestor = [...get_ancestor_ids(type.id)]
              .reverse()
              .find((ancestor_id) => ancestor_id in next_selected_ids);

            if (selected_ancestor) {
              delete next_selected_ids[selected_ancestor];
              const included_leaf_ids = get_leaf_ids_in_subtree(selected_ancestor);
              const excluded_leaf_ids_index = ids_arr_to_index(
                get_leaf_ids_in_subtree(type.id)
              );
              included_leaf_ids.forEach((leaf_id) => {
                if (!(leaf_id in excluded_leaf_ids_index)) {
                  next_selected_ids[leaf_id] = true;
                }
              });
              set_pending_selected_ids(next_selected_ids);
              return;
            }

            // Parent can also be "effectively selected" when all descendants are
            // explicitly selected (without this parent being selected directly).
            // In this case, unchecking parent should still clear the whole subtree.
            delete next_selected_ids[type.id];
            const subtree_ids = [type.id, ...get_descendant_ids(type.id)];
            subtree_ids.forEach((subtree_id) => {
              delete next_selected_ids[subtree_id];
            });
            set_pending_selected_ids(next_selected_ids);
            return;
          }

          if (!(type.id in pending_multi_selected_items_ids.value)) {
            return;
          }

          const next_selected_ids = {
            ...pending_multi_selected_items_ids.value,
          } as Record<string, boolean>;
          delete next_selected_ids[type.id];
          const descendants = get_descendant_ids(type.id);
          descendants.forEach((descendant_id) => {
            delete next_selected_ids[descendant_id];
          });
          // Deselecting any node clears its selected ancestors to avoid overlap.
          const ancestors = get_ancestor_ids(type.id);
          ancestors.forEach((ancestor_id) => {
            delete next_selected_ids[ancestor_id];
          });
          set_pending_selected_ids(next_selected_ids);
        } else {
          const next_selected_ids = {
            ...pending_multi_selected_items_ids.value,
          } as Record<string, boolean>;
          // Clicked node wins: remove selected ancestors and descendants.
          const ancestors = get_ancestor_ids(type.id);
          ancestors.forEach((ancestor_id) => {
            delete next_selected_ids[ancestor_id];
          });
          const descendants = get_descendant_ids(type.id);
          descendants.forEach((descendant_id) => {
            delete next_selected_ids[descendant_id];
          });

          next_selected_ids[type.id] = true;
          set_pending_selected_ids(next_selected_ids);
        }
      } else {
        const is_already_selected = type.id in selected_labels_ids.value;
        if (is_already_selected) {
          emit("on_change", [props.default_id]);
        } else {
          emit("on_change", [type.id]);
        }
        show_popup.value = false;
      }
    };

    const on_esc = () => {
      if (query.value.length > 0) {
        query.value = "";
      } else {
        reset_and_close();
      }
    };

    const reset_and_close = () => {
      show_popup.value = false;
      query.value = "";
      pending_multi_selected_items_ids.value = ids_arr_to_index(
        normalize_selected_ids(props.selected as string[])
      );
      reset_open_ids();
    };

    const on_popup_opened = () => {
      if (props.multi) {
        pending_multi_selected_items_ids.value = ids_arr_to_index(
          normalize_selected_ids(props.selected as string[])
        );
      }
      reset_open_ids();
      schedule_render_items();
    };

    return {
      query,
      on_esc,
      is_searching,
      display_items,
      reset_and_close,
      show_popup,
      selected_items_display,
      selected_items_count,
      current_selected_label_ids,
      current_visual_selected_label_ids,
      apply_changes,
      on_change,
      search_items,
      on_popup_opened,
      on_open_details_click,
      open_ids,
      render_items,
    };
  },
});
